import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { HelpCategory } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteHelpCategory } from '../hooks/use-help-category-mutations';

export interface DeleteHelpCategoryDialogProps {
  storeId: string;
  row: HelpCategory | null;
  onOpenChange: (open: boolean) => void;
}

/** Articles keep their rows and simply lose the category. */
export function DeleteHelpCategoryDialog({
  storeId,
  row,
  onOpenChange,
}: DeleteHelpCategoryDialogProps) {
  const { t } = useTranslation(['helpCenter', 'common', 'errors']);
  const remove = useDeleteHelpCategory(storeId);
  const message = useErrorMessage();
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={row !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('helpCenter:deleteCategory.title', { name: row?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('helpCenter:deleteCategory.description', { count: row?.articleCount ?? 0 })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (row) remove.mutate(row.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending
              ? t('helpCenter:deleteCategory.submitting')
              : t('helpCenter:deleteCategory.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
